import { useEffect, useRef, useState } from 'react';
import type { HomeworkAnswerTraceNode, ProblemMoveIntent } from '@wx-weiqi/contracts';
import type { HomeworkStore } from '../stores/homework-store';

/** Freeze the displayed branch at expiry; never substitute the last attempted branch. */
export function captureTimeoutAnswer(
  answer: readonly ProblemMoveIntent[],
  nodes: readonly HomeworkAnswerTraceNode[],
  submittedNodeIndex: number | null,
) {
  return {
    answer: answer.map((move) => ({ ...move })),
    answerTrace: {
      nodes: nodes.map((node) => ({
        parentNodeIndex: node.parentNodeIndex,
        moves: [...node.moves],
      })),
      submittedNodeIndex,
    },
  };
}

export function useHomeworkTimeout(input: {
  store: HomeworkStore;
  cycle: string;
  assignmentId: string | null;
  problemId: string | null;
  elapsedLimitMs: number;
  expired: boolean;
  enabled: boolean;
  pending: string | null;
  answer: readonly ProblemMoveIntent[];
  nodes: readonly HomeworkAnswerTraceNode[];
  submittedNodeIndex: number | null;
}) {
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const [retry, setRetry] = useState(0);
  const task = useRef<{
    cycle: string;
    payload: ReturnType<typeof captureTimeoutAnswer> | null;
    attempted: number;
    saved: boolean;
  }>({ cycle: input.cycle, payload: null, attempted: -1, saved: false });
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  useEffect(() => {
    if (task.current.cycle !== input.cycle) {
      task.current = { cycle: input.cycle, payload: null, attempted: -1, saved: false };
      setStatus('idle');
    }
    if (!input.enabled || !input.expired || input.assignmentId === null || input.problemId === null)
      return;
    const current = task.current;
    current.payload ??= captureTimeoutAnswer(input.answer, input.nodes, input.submittedNodeIndex);
    if (input.pending !== null || current.saved || current.attempted === retry) return;
    current.attempted = retry;
    setStatus('saving');
    const { store, assignmentId, problemId } = input;
    void store
      .submitAnswer(
        assignmentId,
        problemId,
        current.payload.answer,
        current.payload.answerTrace,
        input.elapsedLimitMs,
        'TIMEOUT',
      )
      .then(() => {
        current.saved = true;
        store.clearAnswerDraft(assignmentId);
        if (live.current && task.current === current) setStatus('saved');
      })
      .catch(() => {
        if (live.current && task.current === current) setStatus('failed');
      });
  }, [input, retry]);
  return { status, retry: () => setRetry((value) => value + 1) };
}
